/**
 * SlackChannelPicker — dropdown of the org's Slack channels for any surface
 * that posts via the Threadbot bot token (currently the OOS daily digest).
 *
 * Ported from merch-analyzer's `slack-channel-picker.tsx` and adapted to the
 * IMS design system (Radix Select + tRPC instead of Next.js + fetch). Same
 * UX: a hash-prefixed select, a refresh button (bust the 5-min server cache
 * after inviting the bot to a new channel), and a "type it manually" escape
 * hatch for private channels the bot can't list.
 *
 * Stores the channel ID as the value (Slack requires the ID for private
 * channels); the label shows the friendly #name.
 */
import { useMemo, useState } from 'react';
import { Hash, Loader2, Pencil, RefreshCw } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const SLACK_ID_RE = /^[CDG][A-Z0-9]{8,}$/;

interface Props {
  value: string | null;
  onChange: (next: string | null) => void;
  disabled?: boolean;
  /** Gate the channel fetch (e.g. only when the parent section is enabled). */
  enabled?: boolean;
}

export function SlackChannelPicker({ value, onChange, disabled, enabled = true }: Props) {
  const [manualMode, setManualMode] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const utils = trpc.useUtils();

  // Stable (undefined) input so there's one cache entry; manual refresh is
  // handled imperatively below so the cache-busting flag never persists in
  // state (which would force a server-side cache bust on every background
  // refetch).
  const channelsQuery = trpc.slackThreadbot.listChannels.useQuery(undefined, {
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const channels = useMemo(
    () => channelsQuery.data?.channels ?? [],
    [channelsQuery.data],
  );
  const configured = channelsQuery.data?.configured ?? true;
  const loading = channelsQuery.isLoading || channelsQuery.isFetching || refreshing;

  // Resolve the stored value (an ID) to its friendly name for display, and
  // detect a value that isn't in the listing (private channel, hand-typed,
  // or a stale ID) so we can still anchor it in the dropdown.
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of channels) m.set(c.id, c.name);
    return m;
  }, [channels]);

  const valueMissing = !!value && !nameById.has(value);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // One round-trip: bust the server's 5-min cache with refresh:true, then
      // write the fresh result straight into the stable-key query the picker
      // reads from. (A plain refetch() would re-call with the un-busted
      // undefined input — a wasted second round-trip.)
      const fresh = await utils.slackThreadbot.listChannels.fetch({ refresh: true });
      utils.slackThreadbot.listChannels.setData(undefined, fresh);
    } finally {
      setRefreshing(false);
    }
  };

  if (manualMode) {
    return (
      <div className="space-y-1.5">
        <div className="relative">
          <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none z-10" />
          <Input
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value.replace(/^#/, '') || null)}
            disabled={disabled}
            placeholder="channel-name or C0123456789"
            className="pl-8 font-mono text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() => setManualMode(false)}
          className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          ← back to picker
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <div className="flex gap-1.5">
        <Select
          value={value ?? ''}
          onValueChange={(v) => onChange(v || null)}
          disabled={disabled || loading}
        >
          <SelectTrigger className="flex-1 font-mono text-sm">
            <div className="flex items-center gap-1.5 min-w-0">
              <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <SelectValue placeholder={loading ? 'Loading channels…' : 'Pick a channel…'} />
            </div>
          </SelectTrigger>
          <SelectContent>
            {valueMissing && value && (
              // Anchor a value that isn't in the listing (private / hand-typed)
              // so the trigger renders it instead of going blank.
              <SelectItem value={value}>
                {SLACK_ID_RE.test(value) ? value : `#${value}`}
              </SelectItem>
            )}
            {channels.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                #{c.name}
              </SelectItem>
            ))}
            {!loading && channels.length === 0 && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                {configured
                  ? 'No channels found. Invite Threadbot to a channel, then refresh.'
                  : 'Slack Threadbot not configured (Settings → Integrations → Slack DMs).'}
              </div>
            )}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={disabled || loading}
          title="Refresh the channel list, useful after inviting Threadbot to a new channel"
          className="shrink-0"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>
      <button
        type="button"
        onClick={() => setManualMode(true)}
        className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
      >
        <Pencil className="h-2.5 w-2.5" />
        Can't find your channel? Type it manually
      </button>
    </div>
  );
}
